import {resolvePublicWrapperBase} from "./publicWrapperBase";
import defaultLogo from "../assets/images/logo-new-3.png";

const DEFAULT_THEME_ID = "default";

const hexToRgb = (hex) => {
    const normalized = String(hex || "").replace("#", "").trim();
    if (![3, 6].includes(normalized.length)) return "0, 0, 0";

    const expanded = normalized.length === 3
        ? normalized.split("").map((char) => `${char}${char}`).join("")
        : normalized;
    const value = Number.parseInt(expanded, 16);
    if (!Number.isFinite(value)) return "0, 0, 0";

    return [
        (value >> 16) & 255,
        (value >> 8) & 255,
        value & 255,
    ].join(", ");
};

const createTheme = ({
    id,
    primary,
    primaryDark,
    secondary,
    accent,
    heading,
    text,
    muted,
    pageBg,
    appBg,
    card,
    border,
    borderSolid,
    soft,
    gradient,
    gradientSoft,
    headerBackground,
    locationBackground,
    logoSrc,
    logoHref,
    logoAlt,
}) => ({
    id,
    primary,
    primaryDark,
    secondary,
    accent,
    heading,
    text,
    muted,
    pageBg,
    appBg,
    card,
    border,
    borderSolid,
    soft,
    gradient,
    gradientSoft,
    headerBackground: headerBackground || gradient,
    locationBackground: locationBackground || gradient,
    logoSrc,
    logoHref,
    logoAlt,
    primaryRgb: hexToRgb(primary),
    primaryDarkRgb: hexToRgb(primaryDark || primary),
    secondaryRgb: hexToRgb(secondary),
    accentRgb: hexToRgb(accent || secondary),
    headingRgb: hexToRgb(heading),
});

export const WHITE_LABEL_THEMES = {
    default: createTheme({
        id: "default",
        primary: "#2f6ee8",
        primaryDark: "#1d4fb8",
        secondary: "#20bca9",
        accent: "#59c9b3",
        heading: "#0f2f37",
        text: "#282828",
        muted: "#6b6b6b",
        pageBg: "#f7f8fa",
        appBg: "#eef5ff",
        card: "#ffffff",
        border: "rgba(47, 110, 232, 0.25)",
        borderSolid: "#e2e7ec",
        soft: "rgba(47, 110, 232, 0.08)",
        gradient: "linear-gradient(120deg, #20bca9 0%, #2f6ee8 100%)",
        gradientSoft: "linear-gradient(120deg, rgba(32, 188, 169, 0.12) 0%, rgba(47, 110, 232, 0.12) 100%)",
        logoSrc: defaultLogo,
        logoHref: "/",
        logoAlt: "NewWay Car Rental",
    }),
    kav2go: createTheme({
        id: "kav2go",
        primary: "#E9392C",
        primaryDark: "#9E2121",
        secondary: "#000000",
        accent: "#E8382B",
        heading: "#2E2E2E",
        text: "#373636",
        muted: "#717171",
        pageBg: "#FFFBFA",
        appBg: "#FFFBFA",
        card: "#ffffff",
        border: "rgba(233, 57, 44, 0.22)",
        borderSolid: "#CCCCCC",
        soft: "rgba(233, 57, 44, 0.1)",
        gradient: "linear-gradient(120deg, #E9392C 0%, #E8382B 100%)",
        gradientSoft: "linear-gradient(120deg, rgba(233, 57, 44, 0.12) 0%, rgba(232, 56, 43, 0.12) 100%)",
        logoSrc: "https://kav2go.nl/img/logo2_transparent-400.png",
        logoHref: "https://kav2go.nl/",
        logoAlt: "KAV2GO",
    }),
    ensing: createTheme({
        id: "ensing",
        primary: "#231F20",
        primaryDark: "#080707",
        secondary: "#CEA54B",
        accent: "#FEC564",
        heading: "#231F20",
        text: "#231F20",
        muted: "#675B5E",
        pageBg: "#F8F9FA",
        appBg: "#FFFFFF",
        card: "#ffffff",
        border: "rgba(206, 165, 75, 0.34)",
        borderSolid: "#E0E0E0",
        soft: "rgba(206, 165, 75, 0.16)",
        gradient: "linear-gradient(120deg, #231F20 0%, #121011 72%, #CEA54B 100%)",
        gradientSoft: "linear-gradient(120deg, rgba(35, 31, 32, 0.06) 0%, rgba(206, 165, 75, 0.2) 100%)",
        headerBackground: "#231F20",
        locationBackground: "#CEA54B",
        logoSrc: "https://www.ensing.nl/wp-content/themes/ensing/build/images/ENSING.png",
        logoHref: "https://www.ensing.nl/",
        logoAlt: "Ensing Autoverhuur",
    }),
    privateshare: createTheme({
        id: "privateshare",
        primary: "#97C51F",
        primaryDark: "#7AA018",
        secondary: "#000000",
        accent: "#A8D92B",
        heading: "#111111",
        text: "#1A1A1A",
        muted: "#666666",
        pageBg: "#F8FAF0",
        appBg: "#F5F5F0",
        card: "#ffffff",
        border: "rgba(151, 197, 31, 0.28)",
        borderSolid: "#E8E8E0",
        soft: "rgba(151, 197, 31, 0.12)",
        gradient: "linear-gradient(135deg, #97C51F 0%, #7AA018 100%)",
        gradientSoft: "linear-gradient(135deg, rgba(151, 197, 31, 0.14) 0%, rgba(122, 160, 24, 0.12) 100%)",
        headerBackground: "#ffffff",
        locationBackground: "#97C51F",
        logoSrc: "https://privateshare.com/img/-dlOI5E0x--321.jpeg",
        logoHref: "https://privateshare.com/",
        logoAlt: "PrivateShare",
    }),
};

const THEME_BY_HOST = {
    "kav2go.nl": "kav2go",
    "www.kav2go.nl": "kav2go",
    "ensing.nl": "ensing",
    "www.ensing.nl": "ensing",
    "privateshare.com": "privateshare",
    "www.privateshare.com": "privateshare",
};

export const resolveWhiteLabelThemeId = () => {
    if (typeof window === "undefined") return DEFAULT_THEME_ID;

    const queryParams = new URLSearchParams(window.location.search || "");
    const explicitThemeId = String(queryParams.get("theme") || "").trim().toLowerCase();
    if (WHITE_LABEL_THEMES[explicitThemeId]) {
        return explicitThemeId;
    }

    const publicBase = resolvePublicWrapperBase(queryParams);
    try {
        const publicBaseUrl = new URL(publicBase);
        return THEME_BY_HOST[publicBaseUrl.hostname] || DEFAULT_THEME_ID;
    } catch (_error) {
        return DEFAULT_THEME_ID;
    }
};

export const resolveWhiteLabelTheme = (themeId = resolveWhiteLabelThemeId()) => (
    WHITE_LABEL_THEMES[themeId] || WHITE_LABEL_THEMES[DEFAULT_THEME_ID]
);

export const getAppliedWhiteLabelThemeId = () => {
    if (typeof document === "undefined") return resolveWhiteLabelThemeId();

    const appliedThemeId = String(document.documentElement?.dataset?.whiteLabelTheme || "").trim().toLowerCase();
    return WHITE_LABEL_THEMES[appliedThemeId] ? appliedThemeId : resolveWhiteLabelThemeId();
};

export const applyWhiteLabelTheme = (themeId = resolveWhiteLabelThemeId()) => {
    if (typeof document === "undefined") return WHITE_LABEL_THEMES[DEFAULT_THEME_ID];

    const theme = resolveWhiteLabelTheme(themeId);
    const root = document.documentElement;

    const variables = {
        "--wl-primary": theme.primary,
        "--wl-primary-dark": theme.primaryDark,
        "--wl-primary-rgb": theme.primaryRgb,
        "--wl-primary-dark-rgb": theme.primaryDarkRgb,
        "--wl-secondary": theme.secondary,
        "--wl-secondary-rgb": theme.secondaryRgb,
        "--wl-accent": theme.accent,
        "--wl-accent-rgb": theme.accentRgb,
        "--wl-heading": theme.heading,
        "--wl-heading-rgb": theme.headingRgb,
        "--wl-text": theme.text,
        "--wl-muted": theme.muted,
        "--wl-page-bg": theme.pageBg,
        "--wl-app-bg": theme.appBg,
        "--wl-card": theme.card,
        "--wl-border": theme.border,
        "--wl-border-solid": theme.borderSolid,
        "--wl-soft": theme.soft,
        "--wl-gradient": theme.gradient,
        "--wl-gradient-soft": theme.gradientSoft,
        "--wl-header-background": theme.headerBackground,
        "--wl-location-background": theme.locationBackground,
        "--kav-red": theme.primary,
        "--kav-blue": theme.secondary,
        "--kav-bg": theme.appBg,
        "--kav-card": theme.card,
        "--kav-soft": theme.soft,
        "--kav-border": theme.border,
        "--kav-text": theme.text,
        "--kav-muted": theme.muted,
        "--kav-green": theme.secondary,
        "--kav-gradient": theme.gradient,
        "--kav-gradient-soft": theme.gradientSoft,
    };

    Object.entries(variables).forEach(([name, value]) => {
        root.style.setProperty(name, value);
    });
    root.dataset.whiteLabelTheme = theme.id;

    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor) {
        themeColor.setAttribute("content", theme.primary);
    }

    return theme;
};
